import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ProposalQualityScore } from './proposalQualityScore.models';
import {
  initState,
  proposalQualityScoreReducer,
  ProposalQualityScoreState,
} from './proposalQualityScore.reducer';

interface ProposalQualityScoreContextProps {
  qualityScore: ProposalQualityScore | null;
  isLoading: boolean;
  isLoaded: boolean;
  fetchProposalQualityScore: (
    appointmentId: string,
    appointmentProposalId: string,
    options?: { signal?: AbortSignal }
  ) => void;
}

const ProposalQualityScoreContext = createContext<
  ProposalQualityScoreContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ProposalQualityScoreState>;
}>;

export const ProposalQualityScoreProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(proposalQualityScoreReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error && state.error !== 'HttpError: canceled') {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const fetchProposalQualityScore = useCallback(
    async (
      appointmentId: string,
      appointmentProposalId: string,
      options?: { signal?: AbortSignal }
    ) => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }
      dispatch({
        type: 'FETCH_PROPOSAL_QUALITY_SCORE',
        payload: { appointmentProposalId },
      });

      try {
        const qualityScore = await httpClient.get<ProposalQualityScore>(
          `/appointments/${appointmentId}/proposals/${appointmentProposalId}/quality-score`,
          { signal: options?.signal }
        );

        dispatch({
          type: 'FETCH_PROPOSAL_QUALITY_SCORE_SUCCESS',
          payload: { appointmentProposalId, qualityScore },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_PROPOSAL_QUALITY_SCORE_FAILURE',
          payload: { error: `${error}` },
        });
      }
    },
    [httpClient, isOnline]
  );

  const qualityScore = useMemo(
    () =>
      state.selectedAppointmentProposalId
        ? (state.entities[state.selectedAppointmentProposalId] ?? null)
        : null,
    [state.entities, state.selectedAppointmentProposalId]
  );

  return (
    <ProposalQualityScoreContext.Provider
      value={{
        fetchProposalQualityScore,
        qualityScore,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
      }}
    >
      {children}
    </ProposalQualityScoreContext.Provider>
  );
};

export const useProposalQualityScore = () => {
  const context = useContext(ProposalQualityScoreContext);
  if (context === undefined) {
    throw new Error(
      'useProposalQualityScore must be used within a ProposalQualityScoreProvider'
    );
  }
  return context;
};
